export type VlSeatActionStatus =
  | 'success'
  | 'failed'
  | 'skipped'
  | 'unknown'
  | 'unchanged'
  | 'up'
  | 'down';

export type VlActionResultLike = {
  successCount?: number;
  failedCount?: number;
  skippedCount?: number;
  unknownCount?: number;
  message?: string;
  results?: Array<{
    tpId?: string;
    status?: string;
    message?: string;
    code?: string;
  }>;
};

export type VlActionCounts = {
  successCount: number;
  failedCount: number;
  skippedCount: number;
  unknownCount: number;
};

export type VlActionToast = {
  type: 'success' | 'info' | 'error';
  message: string;
};

const UNKNOWN_COPY =
  'Do not retry yet — wait, then use Sync from VL to confirm whether Victory Live processed it.';

export function isVlUnknownStatus(status: string | null | undefined): boolean {
  const value = String(status || '').trim().toLowerCase();
  return value === 'unknown' || value === 'reconciliation_required';
}

export function isVlUnknownUserMessage(message: string | null | undefined): boolean {
  const msg = String(message || '').toLowerCase();
  if (!msg) return false;
  return (
    msg.includes('result is unknown') ||
    (msg.includes('status of') && msg.includes('is unknown')) ||
    msg.includes('reconciliation_required') ||
    msg.includes('verify in victory live before retrying') ||
    msg.includes('do not retry yet') ||
    (msg.includes('timed out') && msg.includes('unknown'))
  );
}

export function countVlActionResult(result: VlActionResultLike | null | undefined): VlActionCounts {
  const rows = result?.results || [];
  const fromUnknownRows = rows.filter((row) => isVlUnknownStatus(row.status)).length;
  const fromFailedRows = rows.filter((row) => String(row.status || '').toLowerCase() === 'failed').length;
  const fromSuccessRows = rows.filter((row) => String(row.status || '').toLowerCase() === 'success').length;
  const fromSkippedRows = rows.filter((row) => String(row.status || '').toLowerCase() === 'skipped').length;

  return {
    successCount: Number(result?.successCount ?? fromSuccessRows) || 0,
    failedCount: Number(result?.failedCount ?? fromFailedRows) || 0,
    skippedCount: Number(result?.skippedCount ?? fromSkippedRows) || 0,
    unknownCount: Number(result?.unknownCount ?? fromUnknownRows) || fromUnknownRows,
  };
}

export function successfulVlTpIds(result: VlActionResultLike | null | undefined): string[] {
  return (result?.results || [])
    .filter((row) => String(row.status || '').toLowerCase() === 'success')
    .map((row) => String(row.tpId || '').trim())
    .filter(Boolean);
}

export function formatVlUnknownMessage(actionLabel: string, detail?: string): string {
  const prefix = detail?.trim()
    ? detail.trim()
    : `Victory Live result is UNKNOWN for ${actionLabel}.`;
  if (/sync from vl/i.test(prefix)) return prefix;
  return `${prefix} ${UNKNOWN_COPY}`;
}

export function summarizeVlActionToast(input: {
  actionLabel: string;
  result: VlActionResultLike;
  successMessage?: string;
  failMessage?: string;
  skipMessage?: string;
  extraFailNote?: string;
}): VlActionToast {
  const counts = countVlActionResult(input.result);
  const skipSuffix = counts.skippedCount ? ` (${counts.skippedCount} skipped)` : '';
  const unknownRow = input.result.results?.find((row) => isVlUnknownStatus(row.status));
  const failedRow = input.result.results?.find(
    (row) => String(row.status || '').toLowerCase() === 'failed',
  );

  if (counts.successCount > 0 && counts.failedCount === 0 && counts.unknownCount === 0) {
    return {
      type: 'success',
      message: (input.successMessage || `${input.actionLabel} succeeded`) + skipSuffix,
    };
  }

  if (counts.unknownCount > 0 && counts.successCount === 0 && counts.failedCount === 0) {
    return {
      type: 'info',
      message: formatVlUnknownMessage(
        input.actionLabel,
        unknownRow?.message ||
          `${counts.unknownCount} seat(s): Victory Live result is UNKNOWN after ${input.actionLabel}.`,
      ),
    };
  }

  if (counts.successCount > 0) {
    const parts = [`${input.actionLabel}: ${counts.successCount} succeeded`];
    if (counts.unknownCount > 0) {
      parts.push(`${counts.unknownCount} unknown — ${UNKNOWN_COPY}`);
    }
    if (counts.failedCount > 0) {
      parts.push(`${counts.failedCount} failed`);
    }
    if (counts.skippedCount > 0) {
      parts.push(`${counts.skippedCount} skipped`);
    }
    return { type: 'info', message: parts.join('; ') };
  }

  if (counts.failedCount > 0) {
    const failMsg =
      input.failMessage ||
      failedRow?.message ||
      `${input.actionLabel} failed for ${counts.failedCount} seat(s).`;
    return {
      type: 'error',
      message: input.extraFailNote ? `${failMsg} ${input.extraFailNote}` : failMsg,
    };
  }

  return {
    type: 'info',
    message:
      input.skipMessage ||
      input.result.message ||
      `No seats were changed by ${input.actionLabel}.`,
  };
}

export function toastForVlCaughtError(
  error: unknown,
  actionLabel: string,
  fallback: string,
): VlActionToast {
  const raw =
    error instanceof Error
      ? error.message
      : typeof (error as any)?.message === 'string'
        ? String((error as any).message)
        : fallback;
  if (isVlUnknownUserMessage(raw) || /timed out/i.test(raw)) {
    return { type: 'info', message: formatVlUnknownMessage(actionLabel, raw) };
  }
  return { type: 'error', message: raw || fallback };
}
